import * as React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';

import { Breadcrumb, BreadcrumbItem, Button } from 'react-bootstrap';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
    Ibuilding,
    Ifacility,
    Ifloor,
    Ijob,
    Ilocation,
    Ioption,
    Iroom,
    ItableFiltersReducer,
    Itile,
    Iuser
} from '../../models';
import { IinitialState } from '../../reducers';
import { getIsMobile } from '../../reducers/configReducer';
import { useTable, useSortBy, useFlexLayout, Column } from 'react-table';
/*
 * hopsital Managers manage their locations
 */
import { Redirect, RouteComponentProps } from 'react-router-dom';
import {
    cloneAnyLocationObject as cloneAnyLocationObjectAction,
    deleteAnyLocation as deleteAnyLocationAction,
    filterLocations as filterLocationsAction,
    saveAnyLocation as saveAnyLocationAction,
    setTableFilter as setTableFilterAction,
    toggleEditLocationModal as toggleEditLocationModalAction,
    updateAnyLocation as updateAnyLocationAction
} from '../../actions/manageLocationActions';
import { emptyTile, initialBuilding, initialFloor, initialJob, initialLoc } from '../../reducers/initialState';

import { Banner } from '../common/Banner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormUtil } from '../common/FormUtil';
import { cleanFacility } from '../../reducers/facilitiesReducer';
import { connect } from 'react-redux';
import { constants } from '../../constants/constants';
import { jobStatusEnum } from '../../models-enums';
import { toastr } from 'react-redux-toastr';
import { selectIsLoading } from '../../reducers/commonReducers';
import styled from 'styled-components';
import { selectFilterConfig } from '../../reducers/manageInventoryReducer';
import { FieldConfig } from 'react-reactive-form';
import EditLocationModal from './EditLocationModal';
import { LoadingOverlay } from '../dashboard/LoadingOverlay';

interface Iprops extends RouteComponentProps<any> {
    // Add your regular properties here
    showEditLocationModal: boolean;
    loading: boolean;
    isMobile: boolean;
}

interface IdispatchProps {
    // Add your dispatcher properties here
    toggleEditLocationModal: typeof toggleEditLocationModalAction;
    saveAnyLocation: typeof saveAnyLocationAction;
    updateAnyLocation: typeof updateAnyLocationAction;
    deleteAnyLocation: typeof deleteAnyLocationAction;
    facilityOptions: Ioption[];
    user: Iuser;
    tableData: Array<Ibuilding | Ifloor | Ilocation | Iroom>;
    facility: Ifacility;
    setTableFilter: typeof setTableFilterAction;
    tableFilters: ItableFiltersReducer;
    selectedBuilding: Ibuilding;
    selectedFloor: Ifloor;
    selectedLocation: Ilocation;
    cloneAnyLocationObject: typeof cloneAnyLocationObjectAction;
    filterLocations: typeof filterLocationsAction;
    selectedJob: Ijob;
    mobileFilterConfig: FieldConfig;
}

const ManageLocationWrapper = styled.div`
    & > .ReactTable {
        max-height: ${(props: any) =>
            props.isBuildingLocation
                ? 'calc(100dvh - 54px - 75.42px - 38px)'
                : 'calc(100dvh - 54px - 75.42px - 38px - 40px)'};
    }
`;

const LocationActions = styled.div`
    display: flex;
    justify-content: ${(props: any) => (props.isMobile ? 'flex-start' : 'flex-end')};
    flex-direction: ${(props: any) => (props.isMobile ? 'row-reverse' : 'row')};
    & > .btn,
    .btn-link {
        padding: ${(props: any) => (props.isMobile ? '5px 12px' : '10px 24px')};
    }
`;

const MobileAddContainerFlex = styled.div`
    width: 100%;
    padding: 16px;
`;

const MobileTableAddButton = styled(Button).attrs({
    variant: 'default'
})`
    background-color: #fff;
    color: #000;
    font-size: 16px;
    height: 43px;
    line-height: 1;
    max-height: 43px;
    padding: 13px 24px;
    width: 100%;

    & > svg {
        margin-right: 9px;
        font-size: 12px;
    }
`;

const MobileBreadcrumb = styled.div`
    display: flex;
    height: 40px;
    justify-content: start;
    align-items: center;
    padding-left: 16px;
    font-size: 14px;
    color: #007bc3;

    & > div > svg {
        margin-right: 20px;
        margin-left: 12px;
        color: #fd9a24;
    }

    & > div > span {
        color: #cccccc;
        padding-left: 7px;
        padding-right: 7px;
    }
`;

const TableAddButton = (props: any) => {
    // Short circuit and use mobile flex container for button.
    const { isMobile, ...rest } = props;
    if (isMobile) {
        return (
            <MobileAddContainerFlex>
                <MobileTableAddButton {...props} />
            </MobileAddContainerFlex>
        );
    }

    return <Button className="table-add-button" variant="link" {...rest} />;
};

const ManageLocation: React.FC<Iprops & IdispatchProps & WithTranslation> = props => {
    const {
        t,
        isMobile,
        tableData,
        tableFilters,
        facility,
        loading,
        selectedJob,
        selectedFloor,
        selectedLocation,
        selectedBuilding,
        showEditLocationModal,
        filterLocations,
        toggleEditLocationModal,
        setTableFilter,
        location,
        deleteAnyLocation
    } = props;

    const [selectedRow] = useState<any>({});
    const [currentTile, setCurrentTile] = useState<Itile>(emptyTile);
    const [selectedItem, setSelectedItem] = useState<any>({});
    const [buttonInAction, setButtonInAction] = useState<boolean>(false);
    const [preventToggleModal, setPreventToggleModal] = useState<boolean>(false);
    const [isCloneMode, setIsCloneMode] = useState<boolean>(false);

    // Helper functions
    const getLocationType = useCallback(() => {
        if (selectedLocation.id.length) {
            return 'Room';
        } else if (selectedFloor.id.length) {
            return 'Location';
        } else if (tableFilters.buildingID) {
            return 'Floor';
        } else {
            return 'Building';
        }
    }, [selectedLocation.id.length, selectedFloor.id.length, tableFilters.buildingID]);

    const getColumnHeader = useCallback(() => {
        if (!isMobile) {
            return 'Name';
        } else {
            return getLocationType();
        }
    }, [isMobile, getLocationType]);

    const handleClone = (locationObject: Ibuilding | Ifloor | Ilocation | Iroom) => {
        if ('facilityID' in locationObject) {
            // BUILDING
            locationObject = facility.buildings.find(item => item.id === locationObject.id) || locationObject;
        } else if ('buildingID' in locationObject) {
            // FLOOR
            locationObject = selectedBuilding.floors.find(floor => floor.id === locationObject.id) || locationObject;
        } else if ('floorID' in locationObject) {
            // LOCATION
            locationObject = selectedFloor.locations.find(item => item.id === locationObject.id) || locationObject;
        } else {
            // ROOM
            locationObject = selectedLocation.rooms.find(item => item.id === locationObject.id) || locationObject;
        }

        // Store the original item for cloning, keeping the original name as default
        const itemForForm = {
            ...locationObject,
            name: locationObject.name
        };

        // Set clone mode and selected item, then open the modal
        setIsCloneMode(true);
        setSelectedItem(itemForForm);
        toggleEditLocationModal();
    };

    const handleEdit = (item: Ibuilding | Ifloor | Ilocation | Iroom) => {
        setIsCloneMode(false);
        setSelectedItem(item);
    };

    const handleDelete = (deletedItem: any) => {
        const toastrConfirmOptions = {
            onOk: () => {
                deleteAnyLocation(deletedItem, facility.id);
            },
            onCancel: () => null,
            okText: t('deleteOk'),
            cancelText: t('common:cancel')
        };
        toastr.confirm(t('deleteConfirm'), toastrConfirmOptions);
    };

    const columns = useMemo((): Column<Ibuilding | Ifloor | Ilocation | Iroom>[] => {
        return [
            {
                id: 'name',
                Header: () => <span>{getColumnHeader()}</span>,
                accessor: 'name',
                ...(isMobile ? {} : { minWidth: 300 })
            },
            {
                id: 'actions',
                Header: '',
                ...(isMobile ? {} : { minWidth: 200 }),
                Cell: ({ row }: any) => (
                    <LocationActions isMobile={isMobile}>
                        <Button
                            variant="link"
                            disabled={loading}
                            onClick={e => {
                                if (!loading) {
                                    e.stopPropagation();
                                    setButtonInAction(true);
                                    handleClone(row.original);
                                }
                            }}
                        >
                            clone
                        </Button>
                        <Button
                            variant="link"
                            disabled={loading}
                            onClick={e => {
                                if (!loading) {
                                    e.stopPropagation();
                                    setButtonInAction(true);
                                    handleEdit(row.original);
                                    toggleEditLocationModal();
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={['far', 'edit']} />
                        </Button>
                        <Button
                            variant="link"
                            disabled={loading}
                            style={{ color: 'red' }}
                            onClick={e => {
                                if (!loading) {
                                    e.stopPropagation();
                                    setButtonInAction(true);
                                    setPreventToggleModal(true);
                                    handleDelete(row.original);
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={['far', 'times']} />
                        </Button>
                    </LocationActions>
                )
            }
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, loading]);

    // Use react-table v7 hooks with sorting and flex layout
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
        {
            columns,
            data: tableData
        },
        useSortBy,
        useFlexLayout
    );

    // Component did mount equivalent
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setCurrentTile(constants.getTileByURL(location.pathname));
        if (tableFilters.facilityID !== facility.id) {
            setTableFilter({
                facilityID: facility.id,
                buildingID: '',
                locationID: '',
                floorID: '',
                page: 0
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, facility.id, tableFilters.facilityID]);

    // Filter locations when facility changes, table filters change, or modal closes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // if we do not have a facility id, do not filter,  there is a redirect in the render statement that will redirect back home
        // Don't refresh visible locations while clone operations are in progress to prevent race conditions
        if (facility.id.length && !loading) {
            filterLocations(facility.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        facility.id,
        tableFilters.buildingID,
        tableFilters.floorID,
        tableFilters.locationID,
        tableFilters.page,
        showEditLocationModal,
        loading
    ]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // scroll top every time a filter changes
        if (tableFilters) {
            const tableDiv = document.getElementsByClassName('rt-tbody');
            if (tableDiv && tableDiv.length) {
                const tableDivElement = tableDiv[0] as HTMLDivElement;
                if (tableDivElement) {
                    tableDivElement.scrollTop = 0;
                }
            }
        }
    }, [tableFilters]);

    // Reset clone mode and button action state when modal closes
    useEffect(() => {
        if (!showEditLocationModal) {
            setIsCloneMode(false);
            setButtonInAction(false);
        }
    }, [showEditLocationModal]);

    // Handle row click
    const getTrProps = (rowInfo: any) => {
        if (rowInfo) {
            return {
                onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                    // Prevent navigation during loading operations (cloning, deleting, etc.)
                    if (loading) {
                        return;
                    }

                    const locationObject = rowInfo.original as Ilocation | Ibuilding | Ifloor | Iroom;
                    if (buttonInAction && !preventToggleModal) {
                        toggleEditLocationModal();
                    } else if (!preventToggleModal) {
                        if ('facilityID' in locationObject) {
                            // BUILDING
                            setTableFilter({
                                facilityID: facility.id,
                                buildingID: locationObject.id,
                                locationID: '',
                                floorID: '',
                                page: 0
                            });
                        } else if ('buildingID' in locationObject) {
                            // FLOOR
                            setTableFilter({
                                locationID: '',
                                floorID: locationObject.id,
                                page: 0
                            });
                        } else if ('floorID' in locationObject) {
                            // LOCATION
                            setTableFilter({
                                locationID: locationObject.id,
                                page: 0
                            });
                        } else {
                            // ROOM - we don't do anything when they select a room...
                        }
                    }
                    setButtonInAction(false);
                    setPreventToggleModal(false);
                },
                style: {
                    background: selectedRow[rowInfo.viewIndex]
                        ? constants.colors[`${currentTile.color}Tr` as keyof typeof constants.colors]
                        : '',
                    pointerEvents: loading ? ('none' as const) : ('auto' as const),
                    opacity: loading ? 0.6 : 1,
                    cursor: loading ? ('not-allowed' as const) : ('pointer' as const)
                }
            };
        } else {
            return {};
        }
    };

    const handleBCClick = (
        item: any,
        lType: string,
        e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLDivElement>
    ) => {
        e.preventDefault();

        // Prevent navigation during loading operations (cloning, deleting, etc.)
        if (loading) {
            return;
        }

        switch (lType) {
            case 'Facility':
                setTableFilter({
                    facilityID: facility.id,
                    buildingID: '',
                    locationID: '',
                    floorID: '',
                    page: 0
                });
                break;
            case 'Building':
                setTableFilter({
                    buildingID: item.id,
                    locationID: '',
                    floorID: '',
                    page: 0
                });
                break;
            case 'Floor':
                setTableFilter({
                    locationID: '',
                    floorID: item.id,
                    page: 0
                });
                break;
            case 'Location':
                setTableFilter({ locationID: item.id, page: 0 });
                break;
            default:
                break;
        }
    };

    const makeSandwhich = (label: string, handler?: any) => {
        if (typeof handler !== 'undefined') {
            return (
                <BreadcrumbItem active>
                    <a
                        href="#"
                        onClick={handler}
                        style={{
                            pointerEvents: loading ? ('none' as const) : ('auto' as const),
                            opacity: loading ? 0.5 : 1,
                            cursor: loading ? ('not-allowed' as const) : ('pointer' as const)
                        }}
                    >
                        {label}
                    </a>
                </BreadcrumbItem>
            );
        }
        return <BreadcrumbItem active>{label}</BreadcrumbItem>;
    };

    // get breadcrumb path
    const getBreadcrumbs = () => {
        return (
            <Breadcrumb>
                {tableFilters.buildingID ? (
                    <BreadcrumbItem active>
                        <a
                            href="#"
                            onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                                handleBCClick(initialBuilding, 'Facility', e)
                            }
                        >
                            Buildings
                        </a>
                    </BreadcrumbItem>
                ) : (
                    ''
                )}
                {/* building crumbs */}
                {getLocationType() === 'Floor' && tableFilters.buildingID
                    ? makeSandwhich(selectedBuilding.name)
                    : tableFilters.buildingID
                    ? makeSandwhich(selectedBuilding.name, (e: React.MouseEvent<HTMLAnchorElement>) =>
                          handleBCClick(selectedBuilding, 'Building', e)
                      )
                    : ''}
                {/* Floor crumbs */}
                {getLocationType() === 'Location' && selectedFloor.id.length > 0
                    ? makeSandwhich(selectedFloor.name)
                    : selectedFloor.id
                    ? makeSandwhich(selectedFloor.name, (e: React.MouseEvent<HTMLAnchorElement>) =>
                          handleBCClick(selectedFloor, 'Floor', e)
                      )
                    : ''}
                {/* Location crumbs */}
                {getLocationType() === 'Room' && selectedLocation.id.length > 0
                    ? makeSandwhich(selectedLocation.name)
                    : selectedLocation.id
                    ? makeSandwhich(selectedLocation.name, (e: React.MouseEvent<HTMLAnchorElement>) =>
                          handleBCClick(selectedLocation, 'Location', e)
                      )
                    : ''}
            </Breadcrumb>
        );
    };

    const getMobileBreadcrumbs = () => {
        return (
            <MobileBreadcrumb>
                <div
                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                        handleBCClick(initialBuilding, 'Facility', event);
                    }}
                >
                    <FontAwesomeIcon icon={'chevron-left'} />
                </div>
                {selectedBuilding && (
                    <div
                        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                            handleBCClick(selectedBuilding, 'Building', event);
                        }}
                    >
                        {selectedBuilding.name}
                    </div>
                )}
                {selectedFloor && selectedFloor.name && (
                    <div
                        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                            handleBCClick(selectedFloor, 'Floor', event);
                        }}
                    >
                        <span>/</span> {selectedFloor.name}
                    </div>
                )}
                {selectedLocation && selectedLocation.name && (
                    <div
                        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                            handleBCClick(selectedFloor, 'Floor', event);
                        }}
                    >
                        <span>/</span> {selectedLocation.name}
                    </div>
                )}
            </MobileBreadcrumb>
        );
    };

    // Handle pagination and sorting
    // const onPageChange = (page: number) => {
    //     props.setTableFilter({ page });
    // };

    // const onPageSizeChange = (newRows: number) => {
    //     props.setTableFilter({ rows: newRows, page: 0 });
    // };

    // const onSortedChanged = (newSorted: any[], column: any, shiftKey: boolean) => {
    //     props.setTableFilter({ sorted: newSorted, page: 0 });
    //     setSelectedRow({});
    // };

    if (!facility.id.length || !selectedJob.id.length || selectedJob.status === jobStatusEnum.completed) {
        return (
            <Redirect
                to={{
                    pathname: '/'
                }}
            />
        );
    }

    return (
        <ManageLocationWrapper
            className="manage-location"
            isBuildingLocation={getLocationType() === 'Building'}
            data-manage="true"
            data-btn="true"
        >
            <div className="upper-wrap" data-menuopen="false">
                <Banner title={t('bannerTitle')} img={currentTile.srcBanner} color={currentTile.color} />
                {!isMobile && getBreadcrumbs()}
                <TableAddButton
                    isMobile={isMobile}
                    onClick={() => {
                        setIsCloneMode(false);
                        setSelectedItem({});
                        toggleEditLocationModal();
                    }}
                >
                    {isMobile && <FontAwesomeIcon icon={['far', 'plus']} />}
                    {t(`manageLocation:new${getLocationType()}`)}
                </TableAddButton>
            </div>
            {isMobile && getLocationType() !== 'Building' && getMobileBreadcrumbs()}
            <div id="table-wrap">
                {/* Table using divs with flexbox layout */}
                <div
                    {...getTableProps()}
                    className={`ReactTable beacon-table manage-locations -highlight ${currentTile.color} flex-layout${
                        props.loading ? ' loading-state' : ''
                    }`}
                >
                    <div className="rt-thead -header sticky-header">
                        {headerGroups.map(headerGroup => (
                            <div
                                {...headerGroup.getHeaderGroupProps()}
                                key={headerGroup.getHeaderGroupProps().key}
                                className="rt-tr"
                            >
                                {headerGroup.headers.map((column: any) => {
                                    const isSorted = column.isSorted;
                                    const isSortedDesc = column.isSortedDesc;
                                    return (
                                        <div
                                            {...column.getHeaderProps()}
                                            key={column.getHeaderProps().key}
                                            className="rt-th"
                                        >
                                            {column.render('Header')}
                                            <span>{isSorted ? (isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                    <div {...getTableBodyProps()} className="rt-tbody">
                        {rows.map((row, i) => {
                            prepareRow(row);
                            const rowProps = getTrProps({ index: i, original: row.original, viewIndex: i });
                            return (
                                <div
                                    {...row.getRowProps()}
                                    key={row.getRowProps().key}
                                    onClick={rowProps.onClick}
                                    style={rowProps.style}
                                    className={`rt-tr ${i % 2 === 0 ? '-odd' : '-even'}`}
                                >
                                    {row.cells.map(cell => {
                                        return (
                                            <div
                                                {...cell.getCellProps()}
                                                key={cell.getCellProps().key}
                                                className="rt-td"
                                            >
                                                {cell.render('Cell')}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <LoadingOverlay loading={loading} loadingText="Cloning location..." />
            {showEditLocationModal && (
                <EditLocationModal
                    selectedItem={selectedItem}
                    selectedType={getLocationType()}
                    colorButton={constants.colors[`${currentTile.color}Button` as keyof typeof constants.colors]}
                    isCloneMode={isCloneMode}
                />
            )}
        </ManageLocationWrapper>
    );
};

const mapStateToProps = (state: IinitialState, ownProps: Iprops) => {
    const selectedJob = state.manageJob.jobsByID[state.manageJob.selectedJob.id] || initialJob;
    // facility location object selectors
    // TODO these can be refactored to a id indexed and the facilitiesReducer can be improved by splitting it up into multiple reducers
    const facility = cleanFacility(state.facilities.facilitiesByID[selectedJob.facilityID]);
    const selectedBuilding =
        facility.buildings.find(building => building.id === state.manageLocation.tableFilters.buildingID) ||
        initialBuilding;
    const selectedFloor =
        selectedBuilding.floors.find(floor => floor.id === state.manageLocation.tableFilters.floorID) || initialFloor;
    const selectedLocation =
        selectedFloor.locations.find(location => location.id === state.manageLocation.tableFilters.locationID) ||
        initialLoc;
    const isMobile: boolean = getIsMobile(state);

    return {
        user: state.user,
        loading: selectIsLoading(state),
        showEditLocationModal: state.manageLocation.showEditLocationModal,
        facilityOptions: FormUtil.convertToOptions(state.user.facilities),
        tableData: state.manageLocation.visibleLocations,
        facility,
        tableFilters: state.manageLocation.tableFilters,
        selectedBuilding,
        selectedFloor,
        selectedLocation,
        selectedJob,
        isMobile,
        mobileFilterConfig: selectFilterConfig(state)
    };
};

export default withTranslation('manageLocation')(
    connect(mapStateToProps, {
        saveAnyLocation: saveAnyLocationAction,
        updateAnyLocation: updateAnyLocationAction,
        deleteAnyLocation: deleteAnyLocationAction,
        toggleEditLocationModal: toggleEditLocationModalAction,
        setTableFilter: setTableFilterAction,
        cloneAnyLocationObject: cloneAnyLocationObjectAction,
        filterLocations: filterLocationsAction
    })(ManageLocation)
);
